import { useState, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { Sparkline } from "@plannel/components/dashboard/KpiCard";
import homeDwService from "@plannel/services/dashboard/home-dw-service";
import { FS } from "@plannel/utils/font-util";
import debug from "@plannel/utils/debug-util";

/*
 * HomeBreakdown — 신문 지면의 모듈 면에서 **리드 지표를 상위 N 으로 쪼개 보여 준다.**
 *
 * 왜 있나: 모듈 면의 리드는 이미 질문을 던지고 있다("재고 위험 조합 45건. 어느 거점·품목인가?").
 *   질문만 하고 답을 안 하면 사용자는 그 화면을 열어 보러 갈 수밖에 없다. 그 답이 여기 있다.
 *   빈 지면을 아무거나로 채우려는 게 아니라, **이미 던진 질문에 답하는 것**이 이 블록의 목적이다.
 *
 * ⚠️ 여기서 판정하지 않는다. 서버가 카드와 **같은 접기 규칙**으로 센 결과를 그대로 그린다
 *   (RpReplenishmentFactMapper.findRpBreakdown 주석 참고). 프런트에서 다시 세거나 걸러내면
 *   카드 숫자와 목록 합계가 어긋나고, 그 순간 둘 중 무엇을 믿을지 알 수 없게 된다.
 *
 * ⚠️ 상위 N 만 받으므로 **합계가 카드 숫자와 다를 수 있다.** 그건 오류가 아니라 잘린 것이라,
 *   남은 몫을 "그 외 N건"으로 명시한다. 말없이 다르면 그게 버그로 보인다.
 */

/*
  리드 줄의 **제목 띠 높이**. 리드(헤드라인+리드문)와 이 분해 목록(제목+축 토글)이 공유해서,
  두 열의 제목이 한 줄에 서고 내용(카드 / 막대)도 같은 선에서 시작한다.
  안 맞추면 막대가 카드보다 먼저 시작하고 제목 높이도 달라 지면이 어긋나 보인다.

  ⚠️ 상수를 지면(HomeBroadsheet)이 아니라 여기 두는 이유는 순환 import 를 피하려는 것뿐이다
    (지면이 이 파일을 import 한다). 의미상 주인은 지면 배치다.
  ⚠️ CSS 그리드 행으로 맞추는 게 정석이지만, 그러려면 제목과 내용을 별도 그리드 아이템으로
    쪼개야 하고 그러면 리드가 바뀔 때 카드의 DOM 부모가 달라져 리마운트 진동이 되살아난다.
  ⚠️ 한계: 헤드라인이 두 줄로 접히면 리드 쪽만 커져 다시 어긋난다(창이 아주 좁을 때).
*/
export const LEAD_TITLE_MIN_H = 76;

const TOP_N = 5;

/*
  기간별 추이(스파크라인)를 그릴 수 있는 지표.

  ⚠️ 여기 없는 지표는 **기간 축 자체가 없다.** 수요 신호(판매추세 이상·예측 편차)는
    조합 단위 스냅샷(3개월/6개월 평균 비교, WAPE)이라 "월별 추이" 라는 게 성립하지 않는다.
    없는 축을 만들어 선을 그리면 화면이 없는 정보를 있는 것처럼 보여 준다.
  ⚠️ 값을 더하면 안 된다 — 카드 숫자는 날짜를 접은 "확인할 대상" 수이고 추이는 버킷별 상태다.
    그래서 합계나 평균을 쓰지 않고 **모양만** 보여 준다.
*/
const TRENDABLE = new Set(["rpOpenExc", "ipSafetyStock", "mpExceptions", "mpCapacity"]);

// 추이 데이터 — Sparkline(KpiCard 공용)이 기대하는 [{ i, v }] 로 바꿔 준다.
const useTrend = (cardKey) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!TRENDABLE.has(cardKey)) { setData(null); return undefined; }
    let alive = true;
    homeDwService.getTrend(cardKey)
      .then((res) => {
        const rows = res?.data ?? [];
        // 점이 하나뿐이면 선이 아니라 점이라 추이로 읽히지 않는다 — 그럴 땐 안 그린다.
        if (alive) setData(rows.length >= 2 ? rows.map((r, i) => ({ i, v: Number(r.v ?? 0) })) : null);
      })
      .catch((e) => { debug(e); if (alive) setData(null); });
    return () => { alive = false; };
  }, [cardKey]);
  return data;
};

const AXIS_LABEL = {
  location: "homeAxisLocation",
  item: "homeAxisItem",
  resource: "homeAxisResource",
  cause: "homeAxisCause",
};

/*
  MP 원인 축은 서버 분해가 필요 없다 — 홈 MP 카드가 이미 쓰는 /mp-order 응답에 원인별 합계가
  들어 있다(자재·능력·데이터·기타). 같은 값을 다시 SQL 로 뽑으면 카드와 갈릴 여지만 생긴다.
*/
const MP_CAUSE_FIELDS = [
  ["materialCause", "homeMpCauseMaterial"],
  ["capacityCause", "homeMpCauseCapacity"],
  ["dataCause", "homeMpCauseData"],
  ["otherCause", "homeMpCauseOther"],
];

const mpCauseRows = (tt) => homeDwService.getMpOrder().then((res) => {
  const d = res?.data ?? {};
  return MP_CAUSE_FIELDS
    .map(([field, key]) => ({ code: field, name: tt(key), cnt: Number(d[field] ?? 0), highCnt: 0 }))
    .filter((r) => r.cnt > 0)
    .sort((a, b) => b.cnt - a.cnt);
});

/*
  리드 카드 → 그 지표를 무엇으로 쪼갤 수 있는가.

  ⚠️ 카드 단위다(모듈 단위가 아니다). 같은 모듈이라도 리드가 무엇이냐에 따라 쪼갤 축이 다르다 —
    MP 는 리드가 '생산 예외' 면 거점·품목·원인이지만 '능력 부하율' 이면 자원(라인)이다.
    fact 그레인이 다르기 때문이고(주문=loc x item, 생산=loc x res x item), 없는 축을 넣으면
    빈 목록이 나온다.
  ⚠️ 여기 없는 카드가 리드가 되면 분해 블록은 그냥 안 나온다(빈 자리를 만들지 않는다).
*/
const BREAKDOWNS = {
  rpOpenExc: {
    axes: ["location", "item"],
    fetch: (axis) => homeDwService.getRpBreakdown(axis, TOP_N).then((r) => r?.data ?? []),
  },
  ipSafetyStock: {
    axes: ["location", "item"],
    fetch: (axis) => homeDwService.getIpBreakdown(axis, TOP_N).then((r) => r?.data ?? []),
  },
  mpExceptions: {
    axes: ["location", "item", "cause"],
    fetch: (axis, tt) => (axis === "cause"
      ? mpCauseRows(tt)
      : homeDwService.getMpBreakdown(axis, TOP_N).then((r) => r?.data ?? [])),
  },
  mpCapacity: {
    axes: ["resource"], ratio: true,
    fetch: () => homeDwService.getMpBreakdown("resource", TOP_N).then((r) => r?.data ?? []),
  },
  /*
    예측 정확도 — 값이 **오차율(%)** 이다(정확도가 아니다). 다른 분해가 전부
    "막대가 길수록 급하다" 라서, 정확도를 그리면 제일 안 맞는 품목이 가장 짧은 막대가 되어
    이 블록만 거꾸로 읽힌다. 산식은 카드와 같은 편이다(정확도 = 100 - 오차율).
  */
  bfAccuracy: {
    axes: ["item"], ratio: true,
    fetch: () => homeDwService.getBfAccuracyBreakdown(TOP_N).then((r) => r?.data ?? []),
  },
  dpSalesTrend: {
    axes: ["item"],
    fetch: () => homeDwService.getDemandBreakdown("trend", TOP_N).then((r) => r?.data ?? []),
  },
  bfDeviation: {
    axes: ["item"],
    fetch: () => homeDwService.getDemandBreakdown("deviation", TOP_N).then((r) => r?.data ?? []),
  },
};

// 축 전환 버튼 — 세그먼트가 아니라 텍스트 토글이다. 지면 안이라 폼 컨트롤이 끼면 무거워 보인다.
//   축이 하나뿐인 카드(품목만 가능한 수요 신호 등)는 고를 게 없으므로 아예 안 그린다.
const AxisToggle = ({ tt, axes, axis, onChange }) => {
  if (axes.length < 2) return null;
  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      {axes.map((a) => (
        <Typography
          key={a} component="button" type="button"
          onClick={() => onChange(a)}
          sx={{
            appearance: "none", background: "none", border: 0, p: 0, cursor: "pointer", font: "inherit",
            fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.06em",
            color: a === axis ? "text.primary" : "text.disabled",
            borderBottom: "2px solid",
            borderColor: a === axis ? "text.primary" : "transparent",
            "&:hover": { color: "text.primary" },
          }}
        >
          {tt(AXIS_LABEL[a])}
        </Typography>
      ))}
    </Box>
  );
};

/*
  이 카드에 분해가 붙어 있나 — 지면이 리드를 반폭으로 줄일지 정할 때 쓴다.
  ⚠️ "행이 실제로 있는가" 까지는 모른다(그건 조회를 해 봐야 안다). 등록돼 있는데 값이 0건이면
    오른쪽 칸이 비는데, 그 경우는 드물다 — 분해가 걸린 카드는 건수가 0 이면 애초에 신호를
    안 내서 리드가 되지 못한다.
*/
export const hasBreakdown = (cardKey) => Boolean(BREAKDOWNS[cardKey]);

const HomeBreakdown = ({ tt, cardKey, total, title, color }) => {
  const spec = BREAKDOWNS[cardKey] ?? null;
  const trend = useTrend(cardKey);
  const [axis, setAxis] = useState(null);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // 리드가 바뀌면 축도 그 카드의 첫 축으로 되돌린다 — 이전 카드의 축이 남아 있으면 빈 목록이 된다.
  const activeAxis = spec && spec.axes.includes(axis) ? axis : spec?.axes[0];

  useEffect(() => {
    if (!spec || !activeAxis) { setRows([]); setLoaded(true); return undefined; }
    let alive = true;
    setLoaded(false);
    Promise.resolve(spec.fetch(activeAxis, tt))
      .then((data) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch((e) => { debug(e); if (alive) setRows([]); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardKey, activeAxis]);

  if (!spec || !loaded || rows.length === 0) return null;

  /*
    "그 외 N건" 은 **건수를 세는 축에서만** 뜻이 있다. 능력 부하율처럼 값이 비율인 축은
    상위 N 을 더해도 총계가 되지 않으므로 남는 몫이라는 개념 자체가 없다.
  */
  const countable = !spec.ratio;
  const shown = rows.reduce((n, r) => n + Number(r.cnt ?? 0), 0);
  const rest = countable && typeof total === "number" ? Math.max(0, total - shown) : 0;
  /*
    막대 기준선. 건수 축은 **상위 1위 대비**(상위끼리의 차이를 보는 게 목적),
    비율 분해(자원 부하율·예측 오차율)는 **100% 대비**다 — 93% 와 100% 를 1위 기준으로 그리면
    93 이 막대 끝까지 차서 "능력을 다 썼다" 처럼 보인다.
  */
  const max = countable
    ? Math.max(...rows.map((r) => Number(r.cnt ?? 0)), 1)
    : Math.max(100, ...rows.map((r) => Number(r.cnt ?? 0)));

  return (
    /*
      칸 전체를 쓴다. 예전엔 maxWidth 로 폭을 묶었는데, 그건 이 블록이 기사 구역 **전폭**을
        차지하던 때 이야기다(라벨이 남는 폭을 다 먹고 막대가 오른쪽 끝으로 밀렸다).
        지금은 리드 옆 반폭 칸에 들어가므로 상한이 오히려 칸을 못 채우게 만들어
        목록 오른쪽에 빈 띠가 남는다. 라벨-막대 사이 공백은 **라벨 고정 열**로 이미 해결됐다.
      flex:1 + width:100% 는 그리드 칸(display:flex)의 자식이라 필요하다 — 없으면 반대로
        내용 폭까지 쪼그라든다.
    */
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, width: "100%" }}>
      {/*
        제목 띠 — 리드의 제목 띠와 **같은 높이**(LEAD_TITLE_MIN_H)를 써서 두 열의 제목이
        한 줄에 서고, 내용(카드 / 막대)도 같은 선에서 시작한다.
      */}
      <Box sx={{
        display: "flex", alignItems: "flex-end", gap: 1, pb: 0.75,
        minHeight: LEAD_TITLE_MIN_H, borderBottom: "1px solid", borderColor: "divider",
      }}>
        <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.1em", color: "text.secondary", textTransform: "uppercase" }}>
          {title}
        </Typography>
        {/* 추이 + 축 토글은 **한 그룹으로 오른쪽에 붙인다.** 각자 ml:auto 를 주면 남는 폭이
            둘 사이로 나뉘어 스파크라인이 한가운데로 떠 버린다.
            스파크라인을 토글보다 앞에 두어, 토글을 누르려다 그래프를 클릭하는 일이 없게 한다. */}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
          {trend && <Sparkline gradientId={`spark-${cardKey}`} data={trend} color={color} />}
          <AxisToggle tt={tt} axes={spec.axes} axis={activeAxis} onChange={setAxis} />
        </Box>
      </Box>

      {rows.map((r) => {
        const cnt = Number(r.cnt ?? 0);
        const high = Number(r.highCnt ?? 0);
        const label = r.name || r.code || "-";
        return (
          <Box key={`${r.code}-${label}`} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.6, borderBottom: "1px solid", borderColor: "divider", minWidth: 0 }}>
            {/* 라벨은 **고정 열**이다. 내용 폭으로 흐르게 두면 줄마다 막대 시작점이 달라져
                길이를 눈으로 비교할 수 없다 — 이 목록의 목적이 그 비교다. */}
            <Tooltip title={r.code && r.name ? `${r.code} · ${r.name}` : label}>
              <Typography sx={{ flex: "0 0 auto", width: { xs: 140, sm: 230 }, fontSize: FS.TITLE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </Typography>
            </Tooltip>
            {/* 막대는 상위 1위 대비 비율이다(전체 대비가 아니다) — 상위끼리의 크기 차이를 보는 게 목적. */}
            <Box sx={{ flex: 1, minWidth: 60, height: 6, bgcolor: (th) => alpha(th.palette.text.primary, 0.08), borderRadius: 3 }}>
              <Box sx={{
                width: `${Math.round((cnt / max) * 100)}%`, height: "100%", borderRadius: 3,
                bgcolor: high > 0 ? "error.main" : "warning.main",
              }} />
            </Box>
            {/* 비율 축은 단위를 붙인다 — 건수와 같은 모양으로 두면 93 을 93건으로 읽는다. */}
            <Typography sx={{ width: 56, textAlign: "right", flexShrink: 0, fontSize: FS.TITLE, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {countable ? cnt.toLocaleString() : `${cnt.toLocaleString()}%`}
            </Typography>
          </Box>
        );
      })}

      {/* 상위 N 만 받았으니 남은 몫을 밝힌다 — 말없이 합계가 다르면 그게 버그로 보인다. */}
      {rest > 0 && (
        <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", pt: 0.75 }}>
          {tt("homeBreakdownRest", { count: rest.toLocaleString() })}
        </Typography>
      )}
    </Box>
  );
};

export default HomeBreakdown;
